import {
  getCountryCallingCode as getCountryCallingCodeLib,
  getCountries,
  isSupportedCountry,
  parsePhoneNumberWithError,
  type CountryCode
} from 'libphonenumber-js';
import { CircleHelp } from 'lucide-react';

/**
 * Gets all country names as a map of iso2 -> name
 * Uses the Intl.DisplayNames API for localized country names
 */
export function getAllCountryNames(): Record<string, string> {
  const countries = getCountries();
  const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

  return countries.reduce(
    (acc, countryCode) => {
      try {
        acc[countryCode] = displayNames.of(countryCode) || countryCode;
      } catch {
        acc[countryCode] = countryCode;
      }
      return acc;
    },
    {} as Record<string, string>
  );
}

/**
 * Gets the flag emoji for a given country code
 * Uses Unicode regional indicator symbols to generate flag emojis
 */
export function getCountryFlag(country?: CountryCode) {
  if (!country) {
    return (
      <span className="inline-flex items-center justify-center">
        <CircleHelp className="w-4 h-4" />
      </span>
    );
  }

  // Convert country code to flag emoji using regional indicator symbols
  // Each letter maps to a regional indicator: A=🇦 (U+1F1E6), B=🇧 (U+1F1E7), etc.
  const countryCode = country.toUpperCase();
  const codePoints = countryCode
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  const flagEmoji = String.fromCodePoint(...codePoints);

  return (
    <span
      className="inline-flex items-center justify-center text-base leading-none"
      title={countryCode}
    >
      {flagEmoji}
    </span>
  );
}

/**
 * Gets the country calling code (e.g., "44" for GB)
 */
export function toCountryCode(value: unknown): CountryCode | undefined {
  if (typeof value !== 'string') return undefined;
  const upper = value.toUpperCase();
  return isSupportedCountry(upper) ? upper : undefined;
}

export function getCountryCallingCode(country?: CountryCode): string {
  if (!country) return '';

  try {
    return getCountryCallingCodeLib(country.toUpperCase() as any);
  } catch {
    return '';
  }
}

/**
 * Gets all available countries from libphonenumber-js
 */
export function getAllCountries(): CountryCode[] {
  return getCountries();
}

/**
 * Switches the country code in a phone number while preserving the national number
 * This is useful when users change the country selector
 */
export function switchCountryCode(
  currentPhoneNumber: string,
  newCountry: CountryCode
): string {
  if (!currentPhoneNumber || !newCountry) return currentPhoneNumber;

  if (isDialCodeOnly(currentPhoneNumber)) {
    return `+${getCountryCallingCode(newCountry)}`;
  }
  if (!currentPhoneNumber.startsWith('+')) {
    // this is for sure not an international number, so we need to add the country code
    return `+${getCountryCallingCode(newCountry)} ${currentPhoneNumber}`;
  }

  try {
    // Parse the current phone number to extract the national number
    const parsed = parsePhoneNumberWithError(currentPhoneNumber);
    if (parsed.nationalNumber) {
      // Create new phone number with new country's dial code + preserved national number
      const newDialCode = getCountryCallingCode(newCountry);
      return `+${newDialCode}${parsed.nationalNumber}`;
    }
    // if the national number is not found, return the original number (as country code switch is not possible)
    return currentPhoneNumber;
  } catch {
    // If parsing fails, return the original number
  }

  return currentPhoneNumber;
}

function isDialCodeOnly(phoneNumber: string): boolean {
  const match = /^\+(\d{1,3})$/.exec(phoneNumber.trim());
  return (
    !!match &&
    getCountries().some(
      country => getCountryCallingCodeLib(country) === match[1]
    )
  );
}
